import {
  computed,
  type ComputedRef,
  ref,
  type Ref,
} from 'vue';

import type { Batteries } from '~/types/equipment';

type BatteriesDataGetter = () => Batteries.Battery[] | undefined;

type BatterySortableColumn =
  | 'brand'
  | 'is_enabled'
  | 'model'
  | 'power';

type SortDirection = 'asc' | 'desc' | 'none';

interface SortState {
  column: 'none' | BatterySortableColumn;
  direction: SortDirection;
}

interface UseTableSortingReturn {
  handleSort: (_column: BatterySortableColumn) => void;
  sortedBatteries: ComputedRef<Batteries.Battery[] | undefined>;
  sortState: Ref<SortState>;
}

export type { BatterySortableColumn };

const SORT_DIRECTIONS = {
  ASC: 'asc' as const,
  DESC: 'desc' as const,
  NONE: 'none' as const,
};

const NUMERIC_COLUMNS: readonly BatterySortableColumn[] = ['power'];
const BOOLEAN_COLUMNS: readonly BatterySortableColumn[] = ['is_enabled'];

const DEFAULT_NUMERIC_VALUE = 0;
const DEFAULT_STRING_VALUE = '';
const ENABLED_TRUE_VALUE = 1;
const ENABLED_FALSE_VALUE = 0;

const isNumericColumn = (column: BatterySortableColumn): boolean => (
  NUMERIC_COLUMNS.includes(column)
);

const isBooleanColumn = (column: BatterySortableColumn): boolean => (
  BOOLEAN_COLUMNS.includes(column)
);

export default function useTableSorting(batteries: BatteriesDataGetter): UseTableSortingReturn {
  const sortState = ref<SortState>({
    column: SORT_DIRECTIONS.NONE,
    direction: SORT_DIRECTIONS.NONE,
  });

  const getColumnValue = (item: Batteries.Battery, column: string): number | string => {
    if (isNumericColumn(column as BatterySortableColumn)) {
      return (item[column] as number) ?? DEFAULT_NUMERIC_VALUE;
    }

    if (isBooleanColumn(column as BatterySortableColumn)) {
      return item.is_enabled ? ENABLED_TRUE_VALUE : ENABLED_FALSE_VALUE;
    }

    const stringValue = item[column as keyof Batteries.Battery];
    return stringValue?.toString().toLowerCase() ?? DEFAULT_STRING_VALUE;
  };

  const compareValues = (
    firstValue: number | string,
    secondValue: number | string,
    isAscending: boolean,
  ): number => {
    if (typeof firstValue === 'number' && typeof secondValue === 'number') {
      return isAscending ? firstValue - secondValue : secondValue - firstValue;
    }

    const firstStr = String(firstValue);
    const secondStr = String(secondValue);

    return isAscending
      ? firstStr.localeCompare(secondStr)
      : secondStr.localeCompare(firstStr);
  };

  const sortedBatteries = computed(() => {
    const batteryList = batteries();
    if (!batteryList || sortState.value.column === SORT_DIRECTIONS.NONE) {
      return batteryList;
    }

    const isAscending = sortState.value.direction === SORT_DIRECTIONS.ASC;

    return [...batteryList].sort((a, b) => {
      const firstValue = getColumnValue(a, sortState.value.column);
      const secondValue = getColumnValue(b, sortState.value.column);

      return compareValues(firstValue, secondValue, isAscending);
    });
  });

  function handleSort(column: BatterySortableColumn): void {
    if (sortState.value.column === column) {
      sortState.value.direction = sortState.value.direction === SORT_DIRECTIONS.ASC
        ? SORT_DIRECTIONS.DESC
        : SORT_DIRECTIONS.ASC;
    } else {
      sortState.value.column = column;
      sortState.value.direction = SORT_DIRECTIONS.ASC;
    }
  }

  return {
    handleSort,
    sortedBatteries,
    sortState,
  };
}
